import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AUTH_CHANGED_EVENT } from "@/services/authEvents";

export interface Branding {
  customerId: string | null;
  primaryColor: string | null; // hex
  secondaryColor: string | null; // hex
  headerLogoUrl: string | null;
  chatIdleIconUrl: string | null;
  chatIdleRichText?: any | null;
  linkStudentTitle?: string | null;
  linkStudentText?: string | null;
  linkStudentRichText?: any | null;
}

interface BrandingContextValue {
  branding: Branding | null;
  isLoading: boolean;
  error: string | null;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  isLoading: true,
  error: null,
});

// eslint-disable-next-line react-refresh/only-export-components
export function colorToHslVars(input: string | null): string | null {
  if (!input) return null;
  const str = String(input).trim();
  let r = 0,
    g = 0,
    b = 0;
  if (str.startsWith("#")) {
    const clean = str.replace("#", "");
    const expanded =
      clean.length === 3
        ? clean
            .split("")
            .map(c => c + c)
            .join("")
        : clean;
    const bigint = parseInt(expanded, 16);
    if (Number.isNaN(bigint)) return null;
    r = (bigint >> 16) & 255;
    g = (bigint >> 8) & 255;
    b = bigint & 255;
  } else if (str.toLowerCase().startsWith("rgb")) {
    const match = str
      .replace(/\s+/g, "")
      .match(/^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3})(?:,\d*\.?\d+)?\)$/i);
    if (!match) return null;
    r = Math.max(0, Math.min(255, parseInt(match[1], 10)));
    g = Math.max(0, Math.min(255, parseInt(match[2], 10)));
    b = Math.max(0, Math.min(255, parseInt(match[3], 10)));
  } else {
    return null;
  }
  const r1 = r / 255,
    g1 = g / 255,
    b1 = b / 255;
  const max = Math.max(r1, g1, b1),
    min = Math.min(r1, g1, b1);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r1:
        h = (g1 - b1) / d + (g1 < b1 ? 6 : 0);
        break;
      case g1:
        h = (b1 - r1) / d + 2;
        break;
      case b1:
        h = (r1 - g1) / d + 4;
        break;
    }
    h /= 6;
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
}

const API_BASE_URL: string = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    // Subscribe to storage changes so we can refetch branding when user data appears
    const onStorage = (e: StorageEvent) => {
      if (!mounted) return;
      if (!e.key) return;
      if (e.key === "recruit_ai_user_id" || e.key === "recruit_ai_enduser_user_id") {
        void load();
      }
    };
    window.addEventListener("storage", onStorage);

    // Refetch after any same-tab authentication change. The legacy redeem event
    // remains supported for magic-link flows that have not migrated yet.
    const onAuthChanged = () => {
      if (mounted) void load();
    };
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    window.addEventListener("recruit_ai_redeem_done", onAuthChanged);

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const url = `${API_BASE_URL}/api/branding`;
        const resp = await fetch(url, {
          method: "GET",
          credentials: "include",
        });
        if (resp.ok) {
          const data = await resp.json();
          if (!mounted) return;
          setBranding({
            customerId: data.customerId ?? null,
            primaryColor: data.primaryColor ?? null,
            secondaryColor: data.secondaryColor ?? null,
            headerLogoUrl: data.headerLogoUrl ?? null,
            chatIdleIconUrl: data.chatIdleIconUrl ?? null,
            chatIdleRichText: data.chatIdleRichText ?? null,
            linkStudentTitle: data.linkStudentTitle ?? null,
            linkStudentText: data.linkStudentText ?? null,
            linkStudentRichText: data.linkStudentRichText ?? null,
          });
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || "Failed to load branding");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
      window.removeEventListener("recruit_ai_redeem_done", onAuthChanged);
    };
  }, []);

  // Apply CSS variables derived from branding
  useEffect(() => {
    const root = document.documentElement;
    if (!branding) return;
    const primaryHsl = colorToHslVars(branding.primaryColor);
    const secondaryHsl = colorToHslVars(branding.secondaryColor);
    if (primaryHsl) {
      root.style.setProperty("--primary", primaryHsl);
      root.style.setProperty("--accent", primaryHsl);
      root.style.setProperty("--ring", primaryHsl);
    }
    if (secondaryHsl) {
      root.style.setProperty("--secondary", secondaryHsl);
    }
  }, [branding]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding,
      isLoading,
      error,
    }),
    [branding, isLoading, error]
  );

  if (!isLoading && error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-semibold">Unable to connect</h1>
        <p className="text-muted-foreground">
          We're having trouble reaching the server. Please check your connection and try again.
        </p>
        <button
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandingContext(): BrandingContextValue {
  return useContext(BrandingContext);
}
